/**
 * PlanetLayerConfig - Centralized configuration for Planet Layer
 * Following Universal Architecture pattern for configuration management
 * CLEANED VERSION - Only actively used exports
 */

// Centralized color configuration from preset system
import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { getElementColor } from '../../../../../config/chartRenderingColors';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';

/**
 * Core display constants - only used values
 */
export const PLANET_DISPLAY_CONSTANTS = {
    PLANET_TEXT_COLOR: chartColorPresetManager.getCurrentPreset().elements.planetDefault,
    RENDER_THROTTLE_MS: 16, // ~60fps
    DEFAULT_INSET_RATIO: 0.5,
    MAX_INSET_RATIO: 0.8,
    INSET_STEP: 0.05,

    // Planet collision avoidance configuration
    COLLISION_TOLERANCE_DEGREES: 5.0, // If planets are within 5 degrees globally, move to outer radius
    COLLISION_DETECTION_ENABLED: true
} as const;

/**
 * Dynamic degree divisions configuration using current preset
 */
export function getDegDivisionsConfig() {
    const currentPreset = chartColorPresetManager.getCurrentPreset();
    return {
        // Enable/disable degree divisions
        ENABLED: true,
        
        // Visual styling - dynamic colors from current preset
        STROKE_COLOR: currentPreset.degreeLines.divisionStroke,
        STROKE_WIDTH: currentPreset.degreeLines.divisionWidth,
        DIVISION_LENGTH: 16, // Length of each division mark (total length)
        DIVISION_SPACING: 1, // Degrees per division (1 = 30 marks, 2 = 15 marks, 5 = 6 marks, etc.)
        
        // Special markings
        MAJOR_DIVISION_LENGTH: 16, // Length for 5-degree marks (total length)
        MAJOR_DIVISION_WIDTH: currentPreset.degreeLines.majorDivisionWidth,
        MAJOR_DIVISION_COLOR: currentPreset.degreeLines.majorDivision,
        
        // Positioning and centering
        PERPENDICULAR_OFFSET: 0, // Offset from line center (0 = centered on line)
        CENTER_ON_LINE: true, // Center division marks on the line
    };
}

export const DEGREE_DIVISIONS_CONFIG = getDegDivisionsConfig();

/**
 * Simple direct API-based element type detection
 * Uses direct API field comparison instead of complex ElementPreparationEngine
 */
export function getElementType(planetName: string, horaCurrentPlanet?: string, planetFullName?: string): 'planet' | 'lagna' | 'hora' | 'chandra' {
    // Lagna detection by name
    if (planetName === 'Lg' || planetName === 'Lagna') {
        return 'lagna';
    }

    // Hora detection - check both short and full names
    if (horaCurrentPlanet && (planetName === horaCurrentPlanet || planetFullName === horaCurrentPlanet)) {
        return 'hora';
    }

    // Chandra detection by name
    if (planetName === 'Ch' || planetName === 'Chandra') {
        return 'chandra';
    }

    // Default
    return 'planet';
}

/**
 * ENHANCED: Get element color based on type with proper color constants
 * Now uses centralized color configuration
 */
export { getElementColor };

/**
 * HamburgSymbols font character mapping for planets
 * Uses special astrology font for better visual symbols
 */
export const HAMBURG_SYMBOLS = {
    'Sy': 'Q',  // Surya (Sun) - index 81
    'Ch': 'W',  // Chandra (Moon) - index 87
    'Ma': 'T',  // Mangala (Mars) - index 84
    'Bu': 'E',  // Budha (Mercury) - index 69
    'Gu': 'Y',  // Guru (Jupiter) - index 89
    'Sk': 'R',  // Shukra (Venus) - index 82
    'Sa': 'U',  // Shani (Saturn) - index 85
    'Ra': '{',  // Rahu (North Node) - index 123
    'Ke': '}',  // Ketu (South Node) - index 125
    'Lg': 'Z'   // Lagna (Ascendant) - index 90
} as const;

/**
 * HamburgSymbols font character mapping for Rashi (zodiac signs)
 * Indexed by containerIndex (0-11)
 */
export const HAMBURG_RASHI_SYMBOLS = [
    'c',  // containerIndex 0 = Meena (Pisces)
    'a',  // containerIndex 1 = Mesha (Aries)
    's',  // containerIndex 2 = Vrishabha (Taurus)
    'd',  // containerIndex 3 = Mithuna (Gemini)
    'f',  // containerIndex 4 = Karka (Cancer)
    'g',  // containerIndex 5 = Simha (Leo)
    'h',  // containerIndex 6 = Kanya (Virgo)
    'j',  // containerIndex 7 = Tula (Libra)
    'k',  // containerIndex 8 = Vrishchika (Scorpio)
    'l',  // containerIndex 9 = Dhanu (Sagittarius)
    'v',  // containerIndex 10 = Makara (Capricorn)
    'x'   // containerIndex 11 = Kumbha (Aquarius)
] as const;

/**
 * Sanskrit Rashi names (text mode)
 * Indexed by containerIndex (0-11)
 */
export const  RASHI_NAMES = [
    'Meena',      // containerIndex 0 = House 12
    'Mesha',      // containerIndex 1 = House 1
    'Vrishabha',  // containerIndex 2 = House 2
    'Mithuna',    // containerIndex 3 = House 3
    'Karka',      // containerIndex 4 = House 4
    'Simha',      // containerIndex 5 = House 5
    'Kanya',      // containerIndex 6 = House 6
    'Tula',       // containerIndex 7 = House 7
    'Vrishchika', // containerIndex 8 = House 8
    'Dhanu',      // containerIndex 9 = House 9
    'Makara',     // containerIndex 10 = House 10
    'Kumbha'      // containerIndex 11 = House 11
] as const;

/**
 * Sanskrit Rashi names in Devanagari script
 * Indexed by containerIndex (0-11)
 */
export const RASHI_NAMES_DEVANAGARI = [
    'मीन',      // containerIndex 0 = Meena (Pisces)
    'मेष',      // containerIndex 1 = Mesha (Aries)
    'वृषभ',     // containerIndex 2 = Vrishabha (Taurus)
    'मिथुन',    // containerIndex 3 = Mithuna (Gemini)
    'कर्क',     // containerIndex 4 = Karka (Cancer)
    'सिंह',     // containerIndex 5 = Simha (Leo)
    'कन्या',    // containerIndex 6 = Kanya (Virgo)
    'तुला',     // containerIndex 7 = Tula (Libra)
    'वृश्चिक',  // containerIndex 8 = Vrishchika (Scorpio)
    'धनु',      // containerIndex 9 = Dhanu (Sagittarius)
    'मकर',      // containerIndex 10 = Makara (Capricorn)
    'कुम्भ'     // containerIndex 11 = Kumbha (Aquarius)
] as const;

export type RashiDisplayMode = 'symbols' | 'full' | 'devanagari';

/**
 * Get Rashi display name based on mode
 */
export function getRashiDisplayName(containerIndex: number, mode: 'symbols' | 'short' | 'full' | 'devanagari'): string {
    if (mode === 'symbols') {
        return HAMBURG_RASHI_SYMBOLS[containerIndex] || RASHI_NAMES[containerIndex];
    }
    if (mode === 'devanagari') {
        return RASHI_NAMES_DEVANAGARI[containerIndex] || RASHI_NAMES[containerIndex];
    }
    return RASHI_NAMES[containerIndex];
}

/**
 * Unicode fallback symbols (used when HamburgSymbols font not available)
 */
export const UNICODE_SYMBOLS = {
    'Sy': '☉',  // Surya (Sun)
    'Ch': '☽',  // Chandra (Moon)
    'Ma': '♂',  // Mangala (Mars)
    'Bu': '☿',  // Budha (Mercury)
    'Gu': '♃',  // Guru (Jupiter)
    'Sk': '♀',  // Shukra (Venus)
    'Sa': '♄',  // Shani (Saturn)
    'Ra': '☊',  // Rahu (North Node)
    'Ke': '☋',  // Ketu (South Node)
    'Lg': '⌂'   // Lagna (House symbol)
} as const;

/**
 * ENHANCED: Get planet display name for specific mode with proper symbols support
 * SAFETY: Enhanced to fix symbols mode bug (was returning short names instead of symbols)
 */
export function getPlanetDisplayName(graha: string, mode: 'symbols' | 'short' | 'full'): string {
    // Use HamburgSymbols by default (requires HamburgSymbols font)
    const PLANET_SYMBOLS = HAMBURG_SYMBOLS;

    const PLANET_FULL_NAMES = {
        'Sy': 'Surya',
        'Ch': 'Chandra',
        'Ma': 'Mangala',
        'Bu': 'Budha',
        'Gu': 'Guru',
        'Sk': 'Shukra',
        'Sa': 'Shani',
        'Ra': 'Rahu',
        'Ke': 'Ketu',
        'Lg': 'Lagna'
    } as const;

    const PLANET_SHORT_NAMES = {
        'Sy': 'Sy', 'Sun': 'Sy', 'Surya': 'Sy',
        'Ch': 'Ch', 'Moon': 'Ch', 'Chandra': 'Ch',
        'Ma': 'Ma', 'Mars': 'Ma', 'Mangala': 'Ma',
        'Bu': 'Bu', 'Mercury': 'Bu', 'Budha': 'Bu',
        'Gu': 'Gu', 'Jupiter': 'Gu', 'Guru': 'Gu',
        'Sk': 'Sk', 'Venus': 'Sk', 'Shukra': 'Sk',
        'Sa': 'Sa', 'Saturn': 'Sa', 'Shani': 'Sa',
        'Ra': 'Ra', 'Rahu': 'Ra',
        'Ke': 'Ke', 'Ketu': 'Ke',
        'Lg': 'Lg'
    } as const;

    switch (mode) {
        case 'symbols':
            return PLANET_SYMBOLS[graha as keyof typeof PLANET_SYMBOLS] || graha;
        case 'short':
            return PLANET_SHORT_NAMES[graha as keyof typeof PLANET_SHORT_NAMES] || graha;
        case 'full':
            return PLANET_FULL_NAMES[graha as keyof typeof PLANET_FULL_NAMES] || graha;
        default:
            return graha;
    }
}

/**
 * ENHANCED: Apply retrograde formatting with proper exclusions
 */
export function formatDisplayName(displayName: string, isRetrograde: boolean, graha: string): string {
    if (isRetrograde) {
        // Don't apply retrograde formatting to Rahu and Ketu (they're always retrograde)
        if (graha !== 'Ra' && graha !== 'Ke') {
            return `(${displayName})`;
        }
    }
    return displayName;
}

/**
 * UNIFIED: Get formatted planet display name with mode and retrograde support
 * Single source of truth for planet name rendering
 */
export function getFormattedPlanetDisplayName(
    graha: string,
    mode: 'symbols' | 'short' | 'full',
    isRetrograde: boolean = false
): string {
    const displayName = getPlanetDisplayName(graha, mode);
    return formatDisplayName(displayName, isRetrograde, graha);
}

/**
 * ENHANCED: SVG styling utilities for consistent element styling
 */
export interface SVGStyleConfig {
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: string;
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    opacity?: number;
    fillOpacity?: number;
    textAnchor?: string;
    dominantBaseline?: string;
    className?: string;
}

/**
 * ENHANCED: Apply consistent SVG styles to elements
 * Centralizes repetitive SVG attribute setting
 */
export function applySVGStyles(element: SVGElement, styles: SVGStyleConfig): void {
    if (styles.fontSize !== undefined) {
        element.setAttribute('font-size', styles.fontSize.toString());
    }
    if (styles.fontFamily) {
        element.setAttribute('font-family', styles.fontFamily);
    }
    if (styles.fontWeight) {
        element.setAttribute('font-weight', styles.fontWeight);
    }
    if (styles.fill) {
        element.setAttribute('fill', styles.fill);
    }
    if (styles.stroke) {
        element.setAttribute('stroke', styles.stroke);
    }
    if (styles.strokeWidth !== undefined) {
        element.setAttribute('stroke-width', styles.strokeWidth.toString());
    }
    if (styles.opacity !== undefined) {
        element.setAttribute('opacity', styles.opacity.toString());
    }
    if (styles.fillOpacity !== undefined) {
        element.setAttribute('fill-opacity', styles.fillOpacity.toString());
    }
    if (styles.textAnchor) {
        element.setAttribute('text-anchor', styles.textAnchor);
    }
    if (styles.dominantBaseline) {
        element.setAttribute('dominant-baseline', styles.dominantBaseline);
    }
    if (styles.className) {
        element.setAttribute('class', styles.className);
    }
}

/**
 * ENHANCED: Centralized debug logging utilities
 * Replaces duplicate debugLog methods across core components
 */
export interface DebugLogger {
    log(message: string, data?: any): void;
    error(message: string, error?: any): void;
    warn(message: string, data?: any): void;
}

/**
 * ENHANCED: Create centralized debug logger for components
 */
export function createDebugLogger(componentName: string, debugMode = false): DebugLogger {
    return {
        log: (message: string, data?: any) => {
            if (debugMode && process.env.NODE_ENV === 'development') {
                devLog(`🔧 [${componentName}] ${message}`, data || '');
            }
        },
        error: (message: string, error?: any) => {
            if (debugMode && process.env.NODE_ENV === 'development') {
                devError(`❌ [${componentName}] ${message}`, error || '');
            }
        },
        warn: (message: string, data?: any) => {
            if (debugMode && process.env.NODE_ENV === 'development') {
                devWarn(`⚠️ [${componentName}] ${message}`, data || '');
            }
        }
    };
}

/**
 * Degree Line Inset System - Utility functions for square inset functionality
 */
export const DEGREE_LINE_INSET = {
    /**
     * Validate inset ratio is within acceptable bounds
     */
    validateInsetRatio(insetRatio: number): number {
        return Math.max(0, Math.min(insetRatio, PLANET_DISPLAY_CONSTANTS.MAX_INSET_RATIO));
    },
    
    /**
     * Calculate inset offset in pixels from ratio
     */
    calculateInsetOffset(containerSize: number, insetRatio: number): number {
        const validRatio = this.validateInsetRatio(insetRatio);
        return containerSize * validRatio;
    },
    
    /**
     * Get predefined inset ratios for UI controls
     */
    getPredefinedInsetRatios(): Array<{label: string, value: number}> {
        return [
            { label: 'Inner boundary (0%)', value: 0.0 },
            { label: 'Quarter (25%)', value: 0.25 },
            { label: 'Center (50%)', value: 0.5 },
            { label: 'Three-quarter (75%)', value: 0.75 },
            { label: 'Outer boundary (100%)', value: 1.0 }
        ];
    },
    
    /**
     * Get inset description for debugging
     */
    getInsetDescription(insetRatio: number): string {
        const validRatio = this.validateInsetRatio(insetRatio);
        const percentage = (validRatio * 100).toFixed(1);
        return `Inset: ${percentage}% (${validRatio})`;
    }
};

/**
 * Degree Line Inset Configuration - Constants for square inset system
 */
export const DEGREE_LINE_INSET_CONFIG = {
    // Default configuration
    DEFAULT_INSET_RATIO: PLANET_DISPLAY_CONSTANTS.DEFAULT_INSET_RATIO,
    MAX_INSET_RATIO: PLANET_DISPLAY_CONSTANTS.MAX_INSET_RATIO,
    INSET_STEP: PLANET_DISPLAY_CONSTANTS.INSET_STEP,
    
    // Container size (always square)
    CONTAINER_SIZE: 196,
    
    // Debug modes
    DEBUG_INSET_VISUALIZATION: false,
    DEBUG_INSET_COORDINATES: false,
    
    // Inset calculation examples (for documentation)
    EXAMPLES: {
        // 196px container with 0% = inner boundary (0px from edge)
        CONTAINER_196_INSET_0: {
            containerSize: 196,
            insetRatio: 0.0,
            insetOffset: 0,
            description: 'Inner boundary - lines at inner edge of houses'
        },
        // 196px container with 50% = center (98px from edge)
        CONTAINER_196_INSET_50: {
            containerSize: 196,
            insetRatio: 0.5,
            insetOffset: 98,
            description: 'Center - lines at middle of houses (user example)'
        },
        // 196px container with 100% = outer boundary (196px from edge)
        CONTAINER_196_INSET_100: {
            containerSize: 196,
            insetRatio: 1.0,
            insetOffset: 196,
            description: 'Outer boundary - lines at outer edge of houses'
        }
    }
} as const;

/**
 * Get dynamic secondary data sprite configuration using current preset
 */
export function getSecondaryDataSpriteConfig() {
    const currentPreset = chartColorPresetManager.getCurrentPreset();
    return {
        // Visual settings - dynamic colors from current preset
        STROKE_COLOR: currentPreset.degreeLines.mainStroke,
        FILL_COLOR: currentPreset.elements.chandra, // Use chandra color for secondary elements
        TEXT_COLOR: '#ffffff', // Keep white text for readability
    };
}

export const SECONDARY_DATA_SPRITE_CONFIG = {
    // Dynamic colors
    ...getSecondaryDataSpriteConfig(),
    
    // Размеры (такие же как у основных планет)
    RADIUS: 10,                      // радиус круга (как у основных планет)
    STROKE_WIDTH: 2,              // толщина обводки
    FONT_SIZE: 11,                  // размер шрифта (как у основных планет)
    
    // Позиционирование внешнего слоя
    CONNECTION_LINE_LENGTH: 53,     // длина соединительной линии (основные = 22)
    CONNECTION_LINE_WIDTH: 0.8,     // толщина соединительной линии
    EXTERNAL_OFFSET: 30,            // дополнительный отступ от основных планет
    PERPENDICULAR_SIDE: 1,          // сторона перпендикулярного смещения (1 = противоположная основным)
    
    // Управление отображением
    ENABLED: true,                  // включена ли система по умолчанию
    DEBUG_MODE: false,              // режим отладки
    
    // CSS классы
    SPRITE_CLASS: 'secondary-data-sprite',
    GROUP_CLASS: 'secondary-data-sprites-group',
    CIRCLE_CLASS: 'secondary-data-circle',
    TEXT_CLASS: 'secondary-data-text',
    LINE_CLASS: 'secondary-data-connection-line'
} as const;